import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import {
  FormTextarea,
  FormTextfield,
  useForm,
} from '@backoffice-frontend/forms'
import {
  DestinationType,
  type RouteRequirementInput,
  TripOddElementType,
  TripStopType,
  TripType,
} from '@backoffice-frontend/graphql'
import {
  getTypeFromTaxonomy,
  ODD_TYPE_TO_ELEMENT_TYPE,
  useOverwatchMapListTaxonomiesLazyQuery,
  useTripAnnotations,
} from '@backoffice-frontend/overwatch-map-common'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import { usePaceValidators } from '@backoffice-frontend/validators'
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  CopyIcon,
  InfoIIcon,
  PenIcon,
  PlusIcon,
  SettingsIcon,
  XIcon,
} from '@moia-dev/pace-icons'
import {
  Badge,
  Button,
  Checkbox,
  Heading,
  Icon,
  IconButton,
  LoadingIndicator,
  Option,
  Select,
  Switch,
  Text,
  useDialog,
} from '@moia-dev/pace-web'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { RouteBuilderAreaRoutes } from '../../RouteBuilderAreaRoutes'
import { AllWaypointsModeIndicator } from '../AllWaypointsModeIndicator'
import { useRouteBuilderSavedTripsByVtrsQuery } from '../SavedTrips/SavedTrips.hook'
import { DEFAULT_MAP_DATA, useSharedMapContext } from '../SharedMapContext'
import {
  buildUpdateSavedTripVariables,
  cameraForBoundsWithPaddingFallback,
  computeMapPadding,
  formatRouteErrorMessage,
  isSerializableOddElementType,
  isVnvTripType,
  METROPOLIS_MAP_NAME_PATTERNS,
  nextWaypointOverflowState,
  parseSdsMessageReason,
  TRIP_ODD_ELEMENT_TYPES,
  UI_CLIENT_METADATA,
} from '../utils'
import { GeneratedRouteSummary } from './GeneratedRouteSummary'
import { useHeadingSelection } from './hooks/useHeadingSelection'
import { useMapBounds } from './hooks/useMapBounds'
import type { RouteBuilderVtrDataQuery } from './TripEditor.hook'
import {
  type RouteBuilderValidateRouteInternalMutation,
  useRouteBuilderAssignTripMutation,
  useRouteBuilderCreateSavedTripMutation,
  useRouteBuilderGenerateRouteForRequirementsMutation,
  useRouteBuilderOddElementsForRouteLazyQuery,
  useRouteBuilderOverwatchFleetsQuery,
  useRouteBuilderUpdateSavedTripMutation,
  useRouteBuilderValidateRouteInternalMutation,
  useRouteBuilderValidateRouteSdsMutation,
  useRouteBuilderValidateTripMutation,
} from './TripEditor.hook'
import type {
  AssigningVehicle,
  MobileyeStatus,
  ODDMapElement,
  SdsViolation,
  ValidatedRoute,
  Waypoint,
} from './types'
import { VtrInlineSelector } from './VtrInlineSelector'
import { VtrRequirements } from './VtrRequirements'
import { VtrSelectionModal } from './VtrSelectionModal'
import { createWaypoint, WaypointList } from './WaypointList'

const initialWaypoints: Waypoint[] = [createWaypoint(), createWaypoint()]

type ValidatedSegment = NonNullable<
  RouteBuilderValidateRouteInternalMutation['validateRouteInternal']
>['segments'][number]
type ValidatedRouteViolation = NonNullable<
  RouteBuilderValidateRouteInternalMutation['validateRouteInternal']
>['routeViolations'][number]

type SegmentMetadata = string[][]

type RouteMetadataFormValues = {
  name: string
  description: string
}

/** Reverse lookup: GraphQL enum value → internal short key (e.g. "ODD_ELEMENT_TYPE_STOP_SIGN" → "STOP_SIGN") */
const ELEMENT_TYPE_VALUE_TO_SHORT_KEY: Record<string, string> =
  Object.fromEntries(
    Object.entries(ODD_TYPE_TO_ELEMENT_TYPE).map(([key, val]) => [val, key]),
  )

const ODD_ELEMENT_TYPE_METADATA_KEY = 'odd_element_type'
// trip-manager reads this off the dispatch to resolve the saved route and its VTR for the
// `trace-id` header it sends the AV platform, and it stays on the trip so the later stop /
// update / PUDO calls resolve too. Without it those all read `unknown-route-id`.
const SAVED_TRIP_ID_METADATA_KEY = 'saved_trip_id'

const TRIP_ODD_ELEMENT_TYPE_KEYS: Partial<Record<TripOddElementType, string>> =
  {
    [TripOddElementType.OddElementTypeAllWayStop]: 'ALL_WAY_STOP',
    [TripOddElementType.OddElementTypeNoTurnOnRed]: 'NO_TURN_ON_RED',
    [TripOddElementType.OddElementTypeOneWay]: 'ONE_WAY',
    [TripOddElementType.OddElementTypeOpsHub]: 'OPS_HUB',
    [TripOddElementType.OddElementTypeRoundabout]: 'ROUNDABOUT',
    [TripOddElementType.OddElementTypeStopSign]: 'STOP_SIGN',
    [TripOddElementType.OddElementTypeTrafficLight]: 'TRAFFIC_LIGHT',
    [TripOddElementType.OddElementTypeYieldSign]: 'YIELD_SIGN',
    [TripOddElementType.OddElementTypeNoEntry]: 'NO_ENTRY',
    [TripOddElementType.OddElementTypePedestrianCrossing]:
      'PEDESTRIAN_CROSSING',
    [TripOddElementType.OddElementTypeRailroadCrossing]: 'RAILROAD_CROSSING',
    [TripOddElementType.OddElementTypeBusStop]: 'BUS_STOP',
    [TripOddElementType.OddElementTypeRightOnRed]: 'RIGHT_ON_RED',
    [TripOddElementType.OddElementTypeSchoolZone]: 'SCHOOL_ZONE',
  }

type SimilarRoute = {
  id: string
  name: string
}

type VtrContext = {
  vtrData: NonNullable<RouteBuilderVtrDataQuery['vtrData']>
  ticketKey: string
  /** Index into cbData that the user confirmed in VtrSelectionModal */
  selectedCbIndex?: number
  similarRoutes?: SimilarRoute[]
}

const getDestinationTypes = (tripType: TripType, index: number) => {
  if (tripType === TripType.TripTypeGoods) {
    return [
      index === 0
        ? DestinationType.DestinationTypeGoodsPickUp
        : DestinationType.DestinationTypeGoodsDropOff,
    ]
  }

  return [DestinationType.DestinationTypeRepositioning]
}

const toTripOddElementType = (type: string | null) =>
  type ? TRIP_ODD_ELEMENT_TYPES[type] : undefined

const fromTripOddElementType = (type: TripOddElementType | string | null) =>
  type && type in TRIP_ODD_ELEMENT_TYPE_KEYS
    ? TRIP_ODD_ELEMENT_TYPE_KEYS[type as TripOddElementType]
    : null

const getMetadataValue = (
  metadata: SegmentMetadata | null | undefined,
  key: string,
) => metadata?.find(([metadataKey]) => metadataKey === key)?.[1] ?? null

const withOddElementMetadata = (
  metadata: SegmentMetadata | null | undefined,
  oddElementType: string | null | undefined,
) => {
  const filteredMetadata = (metadata ?? []).filter(
    ([key]) => key !== ODD_ELEMENT_TYPE_METADATA_KEY,
  )

  if (!oddElementType) return filteredMetadata

  return [...filteredMetadata, [ODD_ELEMENT_TYPE_METADATA_KEY, oddElementType]]
}

const getWaypointOddElementType = (
  oddElementType: TripOddElementType | string | null | undefined,
  metadata: SegmentMetadata | null | undefined,
) =>
  getMetadataValue(metadata, ODD_ELEMENT_TYPE_METADATA_KEY) ??
  fromTripOddElementType(oddElementType ?? null) ??
  null

const VTR_ODD_TYPE_TO_TRIP_ELEMENT: Record<string, TripOddElementType> = {
  Stop: TripOddElementType.OddElementTypeStopSign,
  Yield: TripOddElementType.OddElementTypeYieldSign,
  'Traffic Light': TripOddElementType.OddElementTypeTrafficLight,
  Roundabout: TripOddElementType.OddElementTypeRoundabout,
  'No Turn on Red': TripOddElementType.OddElementTypeNoTurnOnRed,
  'One Way': TripOddElementType.OddElementTypeOneWay,
}

const buildRouteRequirements = (
  cbData: NonNullable<RouteBuilderVtrDataQuery['vtrData']>['cbData'],
  metroId: number,
): RouteRequirementInput[] =>
  cbData.flatMap(cb =>
    cb.oddElementTypes
      .map(t => VTR_ODD_TYPE_TO_TRIP_ELEMENT[t])
      .filter((el): el is TripOddElementType => el != null)
      .map(elementType => ({
        elementType,
        metroId,
        totalEncounterCount:
          (cb.numUniqueElements ?? 1) * (cb.targetNumPerElement ?? 1),
        uniqueElementCount: cb.numUniqueElements ?? 1,
      })),
  )

/**
 * Concatenate per-segment route coordinate arrays into a single polyline,
 * deduplicating the join point between adjacent segments.
 *
 * A plain flatMap leaves a straight-line phantom whenever the routing backend
 * snaps the end of segment N and the start of segment N+1 to different road
 * positions. Skipping index 0 of each subsequent segment removes the duplicate
 * and eliminates the phantom without affecting the displayed route geometry.
 */
const concatSegmentCoords = (
  segments: ReadonlyArray<{
    route?: { coordinates: { latitude: number; longitude: number }[] } | null
  }>,
): [number, number][] => {
  const result: [number, number][] = []
  for (const seg of segments) {
    const coords = seg.route?.coordinates ?? []
    if (coords.length === 0) continue
    const start = result.length === 0 ? 0 : 1
    for (let i = start; i < coords.length; i++) {
      result.push([coords[i].longitude, coords[i].latitude])
    }
  }
  return result
}

export type EditingTrip = {
  id: string
  tripId?: string | null
  name: string
  description?: string | null
  tripType: TripType
  segments: {
    id: string
    sequenceId: number
    metadata?: SegmentMetadata | null
    destination: {
      desiredLocation: {
        latitude: number
        longitude: number
        heading?: number | null
      }
      snappedLocation?: {
        latitude: number
        longitude: number
        heading?: number | null
      } | null
      address?: string | null
      stopType?: string | null
      oddElementType?: string | null
    }
    numPassengers: number
    route?: {
      coordinates: { latitude: number; longitude: number }[]
      etaSeconds: number
      distanceMeters: number
    } | null
    /** Trip execution status — set when showing an active trip's waypoints. */
    status?: 'completed' | 'in_progress' | null
  }[]
  codebeamerId?: string | null
  associatedVtr?: string | null
  assignedVtrs?: { vtr: string; codebeamerIds: string[] }[]
  /** Sequential dispatch: every waypoint is dispatched as an explicit stop. */
  allWaypointsMode?: boolean | null
}

type TripEditorViewProps = {
  metropolisId: number
  metropolisName?: string
  mapCenter: [number, number]
  vtr?: VtrContext | null
  onClearVtr?: () => void
  assigningVehicle?: AssigningVehicle | null
  editingTrip?: EditingTrip | null
  initialOddElements?: ODDMapElement[]
  /** When provided, called instead of navigating to saved-trips (e.g. when embedded in a modal). */
  onDismiss?: () => void
  /** When provided, called by the back arrow and Cancel button instead of onDismiss.
   *  Allows a modal host to show saved trips without closing the modal. */
  onBack?: () => void
  /** Override the header title (e.g. hide vehicle name when embedded in a modal). */
  headerTitle?: string
}

export const TripEditorView = ({
  metropolisId,
  metropolisName,
  mapCenter,
  vtr,
  onClearVtr,
  assigningVehicle,
  editingTrip,
  initialOddElements,
  onDismiss,
  onBack,
  headerTitle,
}: TripEditorViewProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack =
    onDismiss ?? (() => navigate(RouteBuilderAreaRoutes.SavedTrips.route))
  const handleBack =
    onBack ?? (() => navigate(RouteBuilderAreaRoutes.SavedTrips.route))
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { required } = usePaceValidators()

  const isEditing = !!editingTrip?.id

  // localVtr is set when the user picks a ticket via the inline selector.
  // vtr prop (from CreateFromVtrView) takes precedence.
  const [localVtr, setLocalVtr] = useState<VtrContext | null>(null)
  const effectiveVtr = vtr ?? localVtr

  const { data: savedTripsByVtrsData } = useErrorHandlingQuery(
    useRouteBuilderSavedTripsByVtrsQuery,
    {
      variables: {
        associatedVtrs: effectiveVtr ? [effectiveVtr.ticketKey] : [],
      },
      skip: !effectiveVtr,
    },
  )
  const similarRoutes = useMemo(
    () =>
      savedTripsByVtrsData?.savedTripsByVtrs.flatMap(
        entry => entry.savedTrips,
      ) ?? [],
    [savedTripsByVtrsData],
  )

  // Voice notes safety drivers recorded, plotted as a standalone layer so whoever is
  // building a route can see where drivers previously flagged a problem. Fetched
  // through the shared map lib's hook, which also owns the layer, the popup and
  // the filters, so this map and the trip-history map read the same fields.
  //
  // Left unscoped (no trip / no VTR) on purpose — see `useTripAnnotations` for
  // why an unscoped query is both the more useful one here and the only one that
  // matches anything against real data.
  const { annotations: annotationRows } = useTripAnnotations()
  const {
    setMapData,
    setCallbacks,
    setMapOverlay,
    setOverlayOptions,
    mapInstance,
    fullBleedMap,
  } = useSharedMapContext()
  const tripType = editingTrip?.tripType ?? TripType.TripTypeVnv
  const { control, handleSubmit, formState, watch } =
    useForm<RouteMetadataFormValues>({
      defaultValues: {
        name:
          editingTrip?.name ??
          (effectiveVtr ? `Route for ${effectiveVtr.ticketKey}` : 'New Route'),
        description:
          editingTrip?.description ?? effectiveVtr?.vtrData.description ?? '',
      },
      mode: 'onChange',
    })
  const routeName = watch('name')

  const { data: overwatchFleetsData, loading: loadingFleets } =
    useErrorHandlingQuery(useRouteBuilderOverwatchFleetsQuery, {
      variables: {},
    })

  // Fleets whose metropolisId matches the selected metropolis, falling back to all fleets.
  const visibleFleets = useMemo(() => {
    const options = overwatchFleetsData?.overwatchFleets ?? []
    const filtered = options.filter(f => f.metropolisId === metropolisId)
    return filtered.length > 0 ? filtered : options
  }, [overwatchFleetsData, metropolisId])

  // null = no manual selection (auto mode); string = user picked explicitly
  const [selectedFleetId, setSelectedFleetId] = useState<string | null>(null)
  const [fleetSelectOpen, setFleetSelectOpen] = useState(false)

  // Reset manual selection when metropolis changes so the new metro's first fleet is used
  const prevMetropolisIdRef = useRef(metropolisId)
  useEffect(() => {
    if (prevMetropolisIdRef.current !== metropolisId) {
      prevMetropolisIdRef.current = metropolisId
      setSelectedFleetId(null)
    }
  }, [metropolisId])

  // For Austin, preselect the "Austin V&V Fleet" when it exists; otherwise fall
  // back to the first matching fleet.
  const defaultFleet =
    (metropolisName === 'Austin' &&
      visibleFleets.find(
        f => f.name?.trim().toLowerCase() === 'austin v&v fleet',
      )) ||
    visibleFleets[0]
  const autoFleetId = defaultFleet?.integrationId || defaultFleet?.id || ''
  // Always resolves: user pick takes precedence, otherwise the default fleet
  const fleetId = selectedFleetId ?? autoFleetId
  const [waypoints, setWaypoints] = useState<Waypoint[]>(() => {
    if (!editingTrip) return initialWaypoints
    return editingTrip.segments.map(seg => ({
      id: seg.id,
      lat: seg.destination.desiredLocation.latitude,
      lng: seg.destination.desiredLocation.longitude,
      // Fall back to the coordinate string when there's no saved address. Use
      // `||` not `??`: the backend returns an empty string (not null) for
      // waypoints saved without a geocoded address, and `??` would keep that ''
      // and leave the input blank.
      address:
        seg.destination.address ||
        `${seg.destination.desiredLocation.latitude.toFixed(6)}, ${seg.destination.desiredLocation.longitude.toFixed(6)}`,
      validated:
        seg.destination.desiredLocation.latitude != null &&
        seg.destination.desiredLocation.longitude != null,
      oddElementType: getWaypointOddElementType(
        seg.destination.oddElementType,
        seg.metadata,
      ),
      desiredLat: null,
      desiredLng: null,
      // The editor keeps heading in headingDegrees (seeded below from the same
      // segment source), not on the waypoint — nothing here reads this field, so
      // leaving it null keeps headingDegrees the single in-editor source.
      heading: null,
      status: seg.status ?? null,
    }))
  })
  const [validatedRoute, setValidatedRoute] = useState<ValidatedRoute | null>(
    () => {
      if (!editingTrip) return null
      const allCoords = concatSegmentCoords(editingTrip.segments)
      if (allCoords.length === 0) return null
      const totalDist = editingTrip.segments.reduce(
        (sum, seg) => sum + (seg.route?.distanceMeters ?? 0),
        0,
      )
      const totalEta = editingTrip.segments.reduce(
        (sum, seg) => sum + (seg.route?.etaSeconds ?? 0),
        0,
      )
      return {
        coordinates: allCoords,
        totalDistanceMeters: totalDist,
        totalEtaSeconds: totalEta,
        violations: [],
      }
    },
  )
  const [validatedSegments, setValidatedSegments] = useState<
    ValidatedSegment[]
  >([])
  const [oddElements, setOddElements] = useState<ODDMapElement[]>(
    () => initialOddElements ?? [],
  )
  const [fetchingOdds, setFetchingOdds] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showAllWaypoints, setShowAllWaypoints] = useState(false)
  // Follows showAllWaypoints but lags behind — stays at the old value during the
  // animation so gradient / Show All / Show Less can cross-fade smoothly.
  const [visuallyExpanded, setVisuallyExpanded] = useState(false)
  const waypointWrapperRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const addDestinationRef = useRef<HTMLDivElement>(null)
  // Explicit px height driven during expand/collapse animation. null = CSS controls.
  const [waypointAnimHeight, setWaypointAnimHeight] = useState<number | null>(
    null,
  )
  const waypointAnimTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Last measured flex:1 collapsed height — captured while in collapsed mode.
  const collapsedHeightRef = useRef<number>(0)
  // Whether the waypoint list overflows its clipped container (Show All needed).
  // null = not yet measured (constrain but don't show Show All yet).
  // false = measured, no overflow. true = measured, overflow.
  const [waypointListOverflows, setWaypointListOverflows] = useState<
    boolean | null
  >(null)
  const [useStopResolver, setUseStopResolver] = useState(false)
  const [addPassenger, setAddPassenger] = useState(
    () => editingTrip?.segments.some(seg => seg.numPassengers > 0) ?? false,
  )
  const [allWaypointsMode, setAllWaypointsMode] = useState(
    () => editingTrip?.allWaypointsMode ?? false,
  )
  const [mobileyeStatus, setMobileyeStatus] = useState<MobileyeStatus>('idle')
  const [validatedMapName, setValidatedMapName] = useState<string | null>(null)
  const [sdsViolations, setSdsViolations] = useState<SdsViolation[]>([])
  const [routeComputeError, setRouteComputeError] = useState<string | null>(
    null,
  )
  const [generateInMapArea, setGenerateInMapArea] = useState(false)

  const EXPAND_MS = 280

  // Keep collapsedHeightRef in sync and detect overflow whenever in flex:1 mode.
  // Runs after every render so measurements stay current.
  //
  // The measurement goes through nextWaypointOverflowState rather than comparing
  // heights inline: the answer decides whether this container clips or scrolls, and
  // a scrollbar changes the very heights being measured. Without the hysteresis
  // that helper adds, an every-render measurement can flip the answer forever and
  // React tears the editor down with "Maximum update depth exceeded".
  useLayoutEffect(() => {
    if (!showAllWaypoints && waypointAnimHeight === null) {
      const wrapper = waypointWrapperRef.current
      if (wrapper) {
        collapsedHeightRef.current = wrapper.clientHeight
        setWaypointListOverflows(prev =>
          nextWaypointOverflowState(
            prev,
            wrapper.scrollHeight,
            wrapper.clientHeight,
          ),
        )
      }
    }
  })

  // Re-measure whenever the wrapper itself resizes — covers two cases:
  // 1. Panel/window resize (wrapper shrinks/grows with available space)
  // 2. ODD animation: summary grows over 280ms, wrapper (flex:1 sibling) shrinks in tandem.
  //    The wrapper ResizeObserver fires on every animation frame as it shrinks,
  //    so overflow is detected as soon as waypoints no longer fit — no manual timer needed.
  useEffect(() => {
    const wrapper = waypointWrapperRef.current
    if (!wrapper) return
    const observer = new ResizeObserver(() => {
      if (!showAllWaypoints && waypointAnimHeight === null) {
        collapsedHeightRef.current = wrapper.clientHeight
        setWaypointListOverflows(prev =>
          nextWaypointOverflowState(
            prev,
            wrapper.scrollHeight,
            wrapper.clientHeight,
          ),
        )
      }
    })
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [showAllWaypoints, waypointAnimHeight])

  const expandWaypoints = useCallback((onComplete?: () => void) => {
    const wrapper = waypointWrapperRef.current
    if (!wrapper) {
      setShowAllWaypoints(true)
      setVisuallyExpanded(true)
      onComplete?.()
      return
    }
    if (waypointAnimTimer.current) clearTimeout(waypointAnimTimer.current)
    const from = collapsedHeightRef.current || wrapper.clientHeight
    setWaypointAnimHeight(from)
    setShowAllWaypoints(true)
    requestAnimationFrame(() => {
      const to = wrapper.scrollHeight
      setWaypointAnimHeight(to)
      waypointAnimTimer.current = setTimeout(() => {
        setWaypointAnimHeight(null)
        setVisuallyExpanded(true)
        onComplete?.()
      }, EXPAND_MS + 20)
    })
  }, [])

  const collapseWaypoints = useCallback(() => {
    const wrapper = waypointWrapperRef.current
    if (!wrapper) {
      setShowAllWaypoints(false)
      setVisuallyExpanded(false)
      return
    }
    if (waypointAnimTimer.current) clearTimeout(waypointAnimTimer.current)
    const from = wrapper.clientHeight
    const to =
      collapsedHeightRef.current || Math.round(wrapper.clientHeight * 0.4)
    // Flip visual state at the start so gradient/buttons begin cross-fading immediately
    setVisuallyExpanded(false)
    // Pin at expanded height
    setWaypointAnimHeight(from)
    requestAnimationFrame(() => {
      // Animate down to the last known collapsed height
      setWaypointAnimHeight(to)
      waypointAnimTimer.current = setTimeout(() => {
        setShowAllWaypoints(false)
        setWaypointAnimHeight(null)
      }, EXPAND_MS + 20)
    })
  }, [])

  const scrollToAddDestination = useCallback(() => {
    const container = scrollContainerRef.current
    const btn = addDestinationRef.current
    if (!container || !btn) return
    const containerRect = container.getBoundingClientRect()
    const btnRect = btn.getBoundingClientRect()
    if (btnRect.bottom > containerRect.bottom) {
      container.scrollBy({
        top: btnRect.bottom - containerRect.bottom + 8,
        behavior: 'smooth',
      })
    }
  }, [])

  const addWaypoint = useCallback(() => {
    if (validatedRoute && !showAllWaypoints) {
      const wrapper = waypointWrapperRef.current
      // Read live overflow at click time — avoids stale state when ODDs are still animating.
      // Same helper as the measuring effects, so a list already rendering as clipped
      // still counts as overflowing here and the new waypoint animates into view.
      const liveOverflows = wrapper
        ? nextWaypointOverflowState(
            waypointListOverflows,
            wrapper.scrollHeight,
            wrapper.clientHeight,
          )
        : waypointListOverflows !== false
      setWaypoints(prev => [...prev, createWaypoint()])
      if (liveOverflows) {
        expandWaypoints(() => {
          requestAnimationFrame(scrollToAddDestination)
        })
      } else {
        requestAnimationFrame(() =>
          requestAnimationFrame(scrollToAddDestination),
        )
      }
    } else {
      setWaypoints(prev => [...prev, createWaypoint()])
      requestAnimationFrame(() => requestAnimationFrame(scrollToAddDestination))
    }
  }, [
    validatedRoute,
    showAllWaypoints,
    waypointListOverflows,
    expandWaypoints,
    scrollToAddDestination,
  ])

  const mapBounds = useMapBounds(mapInstance)

  const isAssigning = !!assigningVehicle
  const effectiveTripType = isAssigning ? TripType.TripTypeVnv : tripType
  const saving = formState.isSubmitting
  // Sequential dispatch is only wired up for V&V trips, and an ad-hoc assignment
  // has no saved route to carry the flag, so the toggle has no effect there.
  const canSetAllWaypointsMode =
    isVnvTripType(effectiveTripType) && !isAssigning
  const effectiveAllWaypointsMode = allWaypointsMode && canSetAllWaypointsMode

  // Save modal — opened after Generate Route validates successfully
  const saveDialog = useDialog()
  const [showVtrInfo, setShowVtrInfo] = useState(false)

  const {
    pendingHeadingWaypointId,
    headingDegrees,
    headingToastVisible,
    startHeadingSelection,
    prepareDragHeadingSelection,
    confirmHeading,
    removeHeading,
    cancelHeadingSelection,
    setHeadings,
  } = useHeadingSelection(
    editingTrip
      ? Object.fromEntries(
          editingTrip.segments
            .map((seg, i) => {
              const h = seg.destination.desiredLocation.heading
              const id = waypoints[i]?.id
              // h === 0 means backend default (no heading set); skip it
              return h && id ? [id, h] : null
            })
            .filter((entry): entry is [string, number] => entry !== null),
        )
      : {},
  )

  const [fetchTaxonomies] = useOverwatchMapListTaxonomiesLazyQuery({
    errorPolicy: 'ignore',
  })

  const [fetchOddElements] = useRouteBuilderOddElementsForRouteLazyQuery({
    errorPolicy: 'ignore',
    fetchPolicy: 'network-only',
  })

  const handleValidatedRoute = useCallback(
    ({
      segments,
      routeViolations,
    }: {
      segments: ValidatedSegment[]
      routeViolations: ValidatedRouteViolation[]
    }) => {
      const allCoords = concatSegmentCoords(segments)
      const totalDistanceMeters = segments.reduce(
        (sum, seg) => sum + (seg.route?.distanceMeters ?? 0),
        0,
      )
      const totalEtaSeconds = segments.reduce(
        (sum, seg) => sum + (seg.route?.etaSeconds ?? 0),
        0,
      )
      if (allCoords.length === 0) {
        setValidatedRoute(null)
        setRouteComputeError(
          'Route could not be computed. Check that waypoints are reachable.',
        )
        setMobileyeStatus('idle')
        setValidatedMapName(null)
        setValidatedSegments([])
        setOddElements([])
        return
      }

      setRouteComputeError(null)
      setShowAllWaypoints(false)
      setVisuallyExpanded(false)
      setWaypointListOverflows(null)
      setValidatedRoute({
        coordinates: allCoords,
        totalEtaSeconds,
        totalDistanceMeters,
        violations: routeViolations.map(v => ({
          type: v.violationType,
          description: null,
          // Backend legIndex is the 1-based destination waypoint index (legIndex=1 = leg to WP1).
          // Normalize to 0-based source waypoint index so legIndex=1 → 0 (first leg WP0→WP1).
          legIndex: v.legIndex != null ? Math.max(0, v.legIndex - 1) : null,
          coordinates: v.geolocation.map(({ latitude, longitude }) => [
            longitude,
            latitude,
          ]),
        })),
      })
      setMobileyeStatus('idle')
      setValidatedMapName(null)
      setValidatedSegments(segments)

      // Snap waypoints to road-snapped locations and mark as validated
      setWaypoints(prev =>
        prev.map((wp, i) => {
          const snapped =
            segments[i]?.stop?.snappedLocation ?? segments[i]?.destination
          if (!snapped || (wp.lat == null && wp.lng == null)) return wp
          return {
            ...wp,
            desiredLat: wp.lat,
            desiredLng: wp.lng,
            lat: snapped.latitude,
            lng: snapped.longitude,
            address: `${snapped.latitude.toFixed(6)}, ${snapped.longitude.toFixed(6)}`,
            validated: true,
          }
        }),
      )

      // Fetch ODD elements along validated route (always, even when violations exist)
      if (allCoords.length >= 2) {
        setFetchingOdds(true)
        fetchTaxonomies({
          variables: { metroId: metropolisId },
        }).then(taxResult => {
          const taxonomies = taxResult.data?.taxonomies ?? []
          if (taxonomies.length === 0) {
            setFetchingOdds(false)
            return
          }
          fetchOddElements({
            variables: {
              routes: [
                {
                  routeCoordinates: allCoords.map(([lng, lat]) => ({
                    latitude: lat,
                    longitude: lng,
                  })),
                },
              ],
              taxonomies,
              metroId: metropolisId,
            },
          })
            .then(result => {
              const data = result.data
              if (!data) return
              const elements = data.oddElementsForRoute.flatMap(route =>
                route.oddElements
                  .filter(e => e.location)
                  .map(e => {
                    const rawType = e.elementType ?? null
                    const type =
                      (rawType && ELEMENT_TYPE_VALUE_TO_SHORT_KEY[rawType]) ??
                      getTypeFromTaxonomy(e.taxonomy) ??
                      'OTHER'
                    return {
                      id: e.taxonomy,
                      type,
                      location: e.location!,
                      name: null as string | null,
                    }
                  }),
              )
              setOddElements(elements)
            })
            .finally(() => setFetchingOdds(false))
        })
      } else {
        setOddElements([])
        setFetchingOdds(false)
      }
    },
    [fetchOddElements, fetchTaxonomies, metropolisId],
  )

  // When editing a pre-existing trip that was opened directly from the list popup
  // (no detail panel), mapData.oddElements is empty so initialOddElements is [].
  // Fetch ODDs from the route coords so the editor shows them without requiring
  // the user to hit Compute first.
  const initialOddElementsRef = useRef(initialOddElements)
  const fetchTaxonomiesRef = useRef(fetchTaxonomies)
  fetchTaxonomiesRef.current = fetchTaxonomies
  const fetchOddElementsRef = useRef(fetchOddElements)
  fetchOddElementsRef.current = fetchOddElements
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only effect — editingTrip and metropolisId are stable per TripEditorView key
  useEffect(() => {
    if (!editingTrip) return
    if (
      initialOddElementsRef.current &&
      initialOddElementsRef.current.length > 0
    )
      return
    const allCoords = concatSegmentCoords(editingTrip.segments)
    if (allCoords.length < 2) return
    setFetchingOdds(true)
    fetchTaxonomiesRef
      .current({ variables: { metroId: metropolisId } })
      .then(taxResult => {
        const taxonomies = taxResult.data?.taxonomies ?? []
        if (taxonomies.length === 0) {
          setFetchingOdds(false)
          return
        }
        fetchOddElementsRef
          .current({
            variables: {
              routes: [
                {
                  routeCoordinates: allCoords.map(([lng, lat]) => ({
                    latitude: lat,
                    longitude: lng,
                  })),
                },
              ],
              taxonomies,
              metroId: metropolisId,
            },
          })
          .then(result => {
            const data = result.data
            if (!data) return
            const elements = data.oddElementsForRoute.flatMap(route =>
              route.oddElements
                .filter(e => e.location)
                .map(e => {
                  const rawType = e.elementType ?? null
                  const type =
                    (rawType && ELEMENT_TYPE_VALUE_TO_SHORT_KEY[rawType]) ??
                    getTypeFromTaxonomy(e.taxonomy) ??
                    'OTHER'
                  return {
                    id: e.taxonomy,
                    type,
                    location: e.location!,
                    name: null as string | null,
                  }
                }),
            )
            setOddElements(elements)
          })
          .finally(() => setFetchingOdds(false))
      })
  }, [])

  const [validateRoute, { loading: validatingRouteInternal }] =
    useErrorHandlingMutation(useRouteBuilderValidateRouteInternalMutation, {
      onCompleted: data => {
        if (!data.validateRouteInternal) return
        handleValidatedRoute(data.validateRouteInternal)
      },
      onError: error => {
        const errorStr = error.message ?? ''
        const isTimeout =
          errorStr.includes('DEADLINE_EXCEEDED') ||
          errorStr.includes('timed out') ||
          errorStr.includes('timeout')
        setRouteComputeError(
          isTimeout
            ? 'Request timed out. System could not find a valid route. Try adjusting waypoints or expanding the search area.'
            : formatRouteErrorMessage(errorStr) ||
                'Route could not be computed. Check that waypoints are reachable.',
        )
      },
    })

  const [validateTrip, { loading: validatingTrip }] = useErrorHandlingMutation(
    useRouteBuilderValidateTripMutation,
    {
      onCompleted: data => {
        if (!data.validateTrip) return
        handleValidatedRoute(data.validateTrip)
      },
      onError: error => {
        const errorStr = error.message ?? ''
        const isTimeout =
          errorStr.includes('DEADLINE_EXCEEDED') ||
          errorStr.includes('timed out') ||
          errorStr.includes('timeout')
        setRouteComputeError(
          isTimeout
            ? 'Request timed out. System could not find a valid route. Try adjusting waypoints or expanding the search area.'
            : formatRouteErrorMessage(errorStr) ||
                'Route could not be computed. Check that waypoints are reachable.',
        )
      },
    },
  )

  const [generateRoute, { loading: generatingRoute }] =
    useErrorHandlingMutation(
      useRouteBuilderGenerateRouteForRequirementsMutation,
      {
        onCompleted: data => {
          const result = data.generateRouteForRequirements
          if (!result) return
          // Snap waypoints from generated segments, then validate
          const newWaypoints: Waypoint[] = result.segments.map((seg, i) => {
            const loc = seg.stop?.desiredLocation ?? seg.destination
            return {
              id: seg.id,
              lat: loc?.latitude ?? null,
              lng: loc?.longitude ?? null,
              address: seg.stop?.desiredAddress ?? `Waypoint ${i + 1}`,
              validated: false,
              oddElementType:
                fromTripOddElementType(seg.stop?.oddElementType ?? null) ??
                null,
              desiredLat: null,
              desiredLng: null,
              heading: null,
            }
          })
          setWaypoints(newWaypoints)
          const segments = result.segments.map((seg, i) => ({
            id: seg.id,
            sequenceId: seg.sequenceId,
            destination: {
              latitude:
                seg.stop?.desiredLocation?.latitude ??
                seg.destination?.latitude ??
                0,
              longitude:
                seg.stop?.desiredLocation?.longitude ??
                seg.destination?.longitude ??
                0,
            },
            stop: {
              name: seg.stop?.desiredAddress ?? `Waypoint ${i + 1}`,
              desiredLocation: {
                latitude:
                  seg.stop?.desiredLocation?.latitude ??
                  seg.destination?.latitude ??
                  0,
                longitude:
                  seg.stop?.desiredLocation?.longitude ??
                  seg.destination?.longitude ??
                  0,
              },
              desiredAddress: seg.stop?.desiredAddress,
              destinationBehavior: {
                stopType: TripStopType.StopTypeNormal,
                types: getDestinationTypes(effectiveTripType, i),
              },
              oddElementType: seg.stop?.oddElementType ?? undefined,
            },
            metadata: withOddElementMetadata(
              undefined,
              fromTripOddElementType(seg.stop?.oddElementType ?? null),
            ),
            numPassengers: 0,
            protected: true,
          }))
          validateRoute({
            variables: {
              segments,
              useStopResolver,
              fleetId: fleetId || undefined,
              useFleetOsRoute: true,
            },
          })
        },
        onError: error => {
          const errorStr = error.message ?? ''
          const isTimeout =
            errorStr.includes('DEADLINE_EXCEEDED') ||
            errorStr.includes('timed out') ||
            errorStr.includes('timeout')
          setRouteComputeError(
            isTimeout
              ? 'Request timed out. System could not find a valid route. Try adjusting waypoints or expanding the search area.'
              : formatRouteErrorMessage(errorStr) ||
                  'Route could not be computed. Check that waypoints are reachable.',
          )
        },
      },
    )

  const computing = validatingRouteInternal || validatingTrip || generatingRoute

  const [validateSds, { loading: validatingSds }] = useErrorHandlingMutation(
    useRouteBuilderValidateRouteSdsMutation,
    {
      onCompleted: data => {
        const allValidations = data.validateRouteSds?.mapValidations ?? []
        const nonMock = allValidations.filter(
          v => !v.map.toLowerCase().includes('mockmap'),
        )

        // If only one non-mock validation, use it directly.
        // If multiple, fall back to metro pattern filtering.
        let validations = nonMock
        if (nonMock.length > 1) {
          const patterns = METROPOLIS_MAP_NAME_PATTERNS[
            metropolisName ?? ''
          ]?.map(p => p.toLowerCase())
          if (patterns) {
            const filtered = nonMock.filter(v =>
              patterns.some(pattern => v.map.toLowerCase().includes(pattern)),
            )
            if (filtered.length > 0) validations = filtered
          }
        }

        const passed = validations.some(v => v.success)
        setMobileyeStatus(passed ? 'passed' : 'failed')

        const matchedValidation = passed
          ? validations.find(v => v.success)
          : validations.find(v => !v.success)
        setValidatedMapName(matchedValidation?.map ?? null)

        if (!passed) {
          const violations: SdsViolation[] = []
          for (const v of validations) {
            if (v.success) continue
            for (const seg of v.routeSegments) {
              if (seg.drivable) continue
              const coords: [number, number][] = v.route
                .slice(seg.startIndex, seg.endIndex + 1)
                .map(r => [r.longitude, r.latitude])
              violations.push({
                startIndex: seg.startIndex,
                endIndex: seg.endIndex,
                reason: seg.reason ?? 'INVALID',
                mapName: v.map,
                coordinates: coords,
                message: v.message || null,
              })
            }

            // All segments are individually drivable but the route still failed.
            // The undrivable portions lie in the transitions between consecutive segments.
            const hasUndrivableSegments = v.routeSegments.some(
              seg => !seg.drivable,
            )
            if (!hasUndrivableSegments && v.routeSegments.length > 1) {
              const allSegments = v.routeSegments
              for (let i = 0; i < allSegments.length - 1; i++) {
                const gapStart = allSegments[i].endIndex
                const gapEnd = allSegments[i + 1].startIndex
                if (gapEnd > gapStart) {
                  const gapCoords: [number, number][] = v.route
                    .slice(gapStart, gapEnd + 1)
                    .map(r => [r.longitude, r.latitude])
                  if (gapCoords.length >= 2) {
                    violations.push({
                      startIndex: gapStart,
                      endIndex: gapEnd,
                      reason: 'SEGMENT_GAP',
                      mapName: v.map,
                      coordinates: gapCoords,
                      message: v.message || null,
                    })
                  }
                }
              }
            }

            // Fallback: when validation failed but routeSegments is empty (backend
            // couldn't segment the route at all), treat the entire route as undrivable.
            if (v.routeSegments.length === 0 && v.route.length >= 2) {
              const coords: [number, number][] = v.route.map(r => [
                r.longitude,
                r.latitude,
              ])
              const reason = parseSdsMessageReason(v.message)
              violations.push({
                startIndex: 0,
                endIndex: v.route.length - 1,
                reason,
                mapName: v.map,
                coordinates: coords,
                message: v.message || null,
              })
            }
          }
          setSdsViolations(violations)
        } else {
          setSdsViolations([])
        }
      },
      onError: () => {
        setMobileyeStatus('failed')
        setValidatedMapName(null)
        setSdsViolations([])
      },
    },
  )

  const [assignTripMutation, { loading: assigning }] = useErrorHandlingMutation(
    useRouteBuilderAssignTripMutation,
    {
      onCompleted: () => {
        enqueueMoiaSuccessNotification(
          `Assigned trip to vehicle: ${assigningVehicle?.name}`,
        )
        goBack()
      },
    },
  )

  const [createSavedTripMutation, { loading: creatingSavedTrip }] =
    useErrorHandlingMutation(useRouteBuilderCreateSavedTripMutation, {
      onCompleted: data => {
        enqueueMoiaSuccessNotification(
          `Saved route: ${data.createSavedTrip?.name ?? 'Route'}`,
        )
        goBack()
      },
    })

  const [updateSavedTripMutation, { loading: updatingSavedTrip }] =
    useErrorHandlingMutation(useRouteBuilderUpdateSavedTripMutation, {
      // The RouteBuilderSavedRouteDetails query is cache-and-network, so after an
      // edit it serves the stale pre-edit entity first — its segments still hold
      // the old (or missing) route geometry, so the details map draws no line
      // until a full page refresh wipes the cache. Evict the details entity for
      // this route so reopening it forces a clean network fetch with the fresh
      // geometry. The mutation only returns { id, name }, so we key off the id.
      update: (cache, { data }) => {
        const id = data?.updateSavedTrip?.id
        if (!id) return
        cache.evict({
          id: cache.identify({ __typename: 'SavedRouteDetails', id }),
        })
        cache.gc()
      },
      onCompleted: data => {
        enqueueMoiaSuccessNotification(
          `Updated route: ${data.updateSavedTrip?.name ?? 'Route'}`,
        )
        goBack()
      },
    })

  const isSavingSavedTrip = saving || creatingSavedTrip || updatingSavedTrip

  const validWaypoints = waypoints.filter(w => w.lat != null && w.lng != null)
  const canGenerateFromVtr =
    !isAssigning && !!effectiveVtr && validWaypoints.length === 0
  const canCompute = canGenerateFromVtr || validWaypoints.length >= 2
  const hasViolations = (validatedRoute?.violations.length ?? 0) > 0
  // True only when route exists AND list is confirmed to overflow the clipped container.
  // null = not yet measured; false = confirmed no overflow → standard fixed-bottom layout.
  const isCollapsedMode =
    !!validatedRoute &&
    !isAssigning &&
    !showAllWaypoints &&
    waypointListOverflows === true
  const handleCompute = useCallback(() => {
    if (!canCompute) return
    setSdsViolations([])
    setValidatedMapName(null)
    setRouteComputeError(null)
    cancelHeadingSelection(null)
    setWaypoints(prev => prev.filter(w => w.lat != null && w.lng != null))

    // Generate route from VTR requirements when no waypoints are placed
    if (canGenerateFromVtr && effectiveVtr) {
      const cbData =
        effectiveVtr.selectedCbIndex !== undefined
          ? effectiveVtr.vtrData.cbData.slice(
              effectiveVtr.selectedCbIndex,
              effectiveVtr.selectedCbIndex + 1,
            )
          : effectiveVtr.vtrData.cbData
      const routeRequirements = buildRouteRequirements(cbData, metropolisId)
      if (routeRequirements.length > 0) {
        const boundingBox =
          generateInMapArea && mapBounds
            ? {
                southWest: {
                  latitude: mapBounds.getSouth(),
                  longitude: mapBounds.getWest(),
                },
                northEast: {
                  latitude: mapBounds.getNorth(),
                  longitude: mapBounds.getEast(),
                },
              }
            : undefined
        generateRoute({ variables: { routeRequirements, boundingBox } })
        return
      }
      // VTR has no mappable ODD types and no waypoints placed — nothing to do
      return
    }

    const segments = validWaypoints.map((w, i) => ({
      id: w.id,
      sequenceId: i + 1,
      destination: { latitude: w.lat!, longitude: w.lng! },
      stop: {
        name: w.address || `Waypoint ${i + 1}`,
        desiredLocation: {
          latitude: w.lat!,
          longitude: w.lng!,
          heading: headingDegrees[w.id] ?? undefined,
        },
        desiredAddress: w.address || undefined,
        destinationBehavior: {
          stopType: TripStopType.StopTypeNormal,
          types: getDestinationTypes(effectiveTripType, i),
        },
        oddElementType: toTripOddElementType(w.oddElementType),
      },
      metadata: withOddElementMetadata(undefined, w.oddElementType),
      numPassengers: addPassenger && i > 0 ? 1 : undefined,
    }))

    if (isAssigning && assigningVehicle) {
      validateTrip({
        variables: {
          vin: assigningVehicle.vin,
          segments,
          useStopResolver,
        },
      })
      return
    }

    const resolvedFleetId = fleetId || undefined
    validateRoute({
      variables: {
        segments,
        useStopResolver,
        fleetId: resolvedFleetId,
        useFleetOsRoute: true,
      },
    })
  }, [
    canCompute,
    canGenerateFromVtr,
    effectiveVtr,
    metropolisId,
    generateRoute,
    generateInMapArea,
    mapBounds,
    validWaypoints,
    effectiveTripType,
    addPassenger,
    isAssigning,
    assigningVehicle,
    validateTrip,
    useStopResolver,
    validateRoute,
    fleetId,
    cancelHeadingSelection,
    headingDegrees,
  ])

  const handleMobileyeValidation = useCallback(() => {
    if (!validatedRoute || hasViolations || !fleetId) return
    setMobileyeStatus('loading')
    setValidatedMapName(null)
    // validatedRoute.coordinates are in GeoJSON [lng, lat] order
    const route = validatedRoute.coordinates.map(([lng, lat]) => ({
      latitude: lat,
      longitude: lng,
    }))
    validateSds({ variables: { route, fleetId } })
  }, [validatedRoute, hasViolations, fleetId, validateSds])

  const buildSegments = useCallback(() => {
    if (validatedSegments.length > 0) {
      return validatedSegments.map(seg => ({
        id: seg.id,
        sequenceId: seg.sequenceId,
        destination: {
          latitude:
            seg.stop?.desiredLocation?.latitude ??
            seg.destination?.latitude ??
            0,
          longitude:
            seg.stop?.desiredLocation?.longitude ??
            seg.destination?.longitude ??
            0,
        },
        stop: {
          name: seg.stop?.desiredAddress || `Waypoint ${seg.sequenceId}`,
          desiredLocation: {
            latitude:
              seg.stop?.desiredLocation?.latitude ??
              seg.destination?.latitude ??
              0,
            longitude:
              seg.stop?.desiredLocation?.longitude ??
              seg.destination?.longitude ??
              0,
            heading: headingDegrees[seg.id] ?? undefined,
          },
          snappedLocation: seg.stop?.snappedLocation
            ? {
                latitude: seg.stop.snappedLocation.latitude,
                longitude: seg.stop.snappedLocation.longitude,
              }
            : undefined,
          snappedAddress: seg.stop?.snappedAddress,
          desiredAddress: seg.stop?.desiredAddress,
          destinationBehavior: {
            stopType:
              seg.stop?.destinationBehavior?.stopType ??
              TripStopType.StopTypeNormal,
            types:
              seg.stop?.destinationBehavior?.types ??
              getDestinationTypes(effectiveTripType, seg.sequenceId - 1),
          },
          oddElementType: seg.stop?.oddElementType,
        },
        route: seg.route
          ? {
              coordinates: seg.route.coordinates.map(c => ({
                latitude: c.latitude,
                longitude: c.longitude,
              })),
              etaSeconds: seg.route.etaSeconds,
              distanceMeters: seg.route.distanceMeters,
            }
          : undefined,
        metadata: withOddElementMetadata(
          seg.metadata,
          getWaypointOddElementType(seg.stop?.oddElementType, seg.metadata),
        ),
        numPassengers: addPassenger && seg.sequenceId > 1 ? 1 : 0,
        protected: true,
      }))
    }
    return validWaypoints.map((w, i) => ({
      id: w.id,
      sequenceId: i + 1,
      destination: { latitude: w.lat!, longitude: w.lng! },
      stop: {
        name: w.address || `Waypoint ${i + 1}`,
        desiredLocation: {
          latitude: w.lat!,
          longitude: w.lng!,
          heading: headingDegrees[w.id] ?? undefined,
        },
        desiredAddress: w.address || undefined,
        destinationBehavior: {
          stopType: TripStopType.StopTypeNormal,
          types: getDestinationTypes(effectiveTripType, i),
        },
        oddElementType: toTripOddElementType(w.oddElementType),
      },
      metadata: withOddElementMetadata(undefined, w.oddElementType),
      numPassengers: addPassenger && i > 0 ? 1 : 0,
      protected: true,
    }))
  }, [
    validatedSegments,
    validWaypoints,
    effectiveTripType,
    addPassenger,
    headingDegrees,
  ])

  const handleAssign = useCallback(() => {
    if (!isAssigning || !canCompute || hasViolations) return
    const segments = buildSegments()
    // Present when a saved route was opened to dispatch it, absent for waypoints dropped
    // straight onto the map. Waypoints may have been edited before assigning — this records
    // where the route came from, not that it was dispatched byte for byte.
    //
    // Gated on `tripId`, not on `id` alone: `id` is a saved-route id only when the editor was
    // opened from a saved route, and both of those flows also set `tripId`. Reassigning a live
    // trip (VehicleDetailsPanel) puts the *active trip* id in `id` and no `tripId`, and
    // trip-manager stamps whatever it is given into the route-id segment of the trace-id header
    // even when the lookup misses — so an ungated `id` would send MOIA a header that looks
    // resolvable and isn't.
    const savedTripId =
      editingTrip?.tripId && editingTrip.id ? editingTrip.id : undefined

    assignTripMutation({
      variables: {
        vin: assigningVehicle!.vin,
        segments,
        type: effectiveTripType,
        // Always mark the dispatched trip as written by this UI so Trip
        // History can attribute it (matches the create/update save flows).
        metadata: savedTripId
          ? [...UI_CLIENT_METADATA, [SAVED_TRIP_ID_METADATA_KEY, savedTripId]]
          : UI_CLIENT_METADATA,
        useStopResolver,
      },
    })
  }, [
    effectiveTripType,
    canCompute,
    hasViolations,
    buildSegments,
    isAssigning,
    assigningVehicle,
    assignTripMutation,
    editingTrip,
    useStopResolver,
  ])

  const handleSave = useCallback(
    ({ name, description }: RouteMetadataFormValues) => {
      if (isAssigning || !canCompute || hasViolations) return
      const segments = buildSegments()
      const trimmedName = name.trim()
      const trimmedDescription = description.trim()
      const assignedVtrs =
        editingTrip?.assignedVtrs ??
        (effectiveVtr
          ? [
              {
                vtr: effectiveVtr.ticketKey,
                codebeamerIds: effectiveVtr.vtrData.cbData
                  .map(cb => cb.codebeamerTestcaseId)
                  .filter(codebeamerId => codebeamerId.length > 0),
              },
            ]
          : undefined)
      if (isEditing && editingTrip) {
        updateSavedTripMutation({
          variables: buildUpdateSavedTripVariables({
            id: editingTrip.id,
            tripId: editingTrip.tripId,
            name: trimmedName,
            description: trimmedDescription,
            tripType: effectiveTripType,
            segments,
            assignedVtrs,
            allWaypointsMode: effectiveAllWaypointsMode,
          }),
        })
        return
      }

      createSavedTripMutation({
        variables: {
          name: trimmedName,
          type: effectiveTripType,
          segments,
          metroId: metropolisId,
          description: trimmedDescription || undefined,
          metadata: UI_CLIENT_METADATA,
          assignedVtrs,
          allWaypointsMode: effectiveAllWaypointsMode,
        },
      })
    },
    [
      buildSegments,
      canCompute,
      createSavedTripMutation,
      editingTrip,
      effectiveAllWaypointsMode,
      effectiveTripType,
      hasViolations,
      isAssigning,
      isEditing,
      metropolisId,
      updateSavedTripMutation,
      effectiveVtr,
    ],
  )

  const handleCopyLocations = useCallback(() => {
    const text = validWaypoints
      .map(w => {
        const h = headingDegrees[w.id]
        const parts = [w.lat!.toFixed(6), w.lng!.toFixed(6)]
        if (h !== undefined) parts.push(String(h))
        // Only append tokens the parser accepts; an unrecognized type would make
        // parseCoordinates reject the whole line on paste, silently dropping the
        // coordinates. Omitting it keeps the point (without its icon) instead.
        if (w.oddElementType && isSerializableOddElementType(w.oddElementType))
          parts.push(w.oddElementType)
        return parts.join(', ')
      })
      .join('\n')
    navigator.clipboard.writeText(text)
    enqueueMoiaSuccessNotification('Coordinates copied to clipboard')
  }, [validWaypoints, headingDegrees, enqueueMoiaSuccessNotification])

  const isDuplicate = useCallback(
    (lat: number, lng: number) => {
      const round = (n: number) => Math.round(n * 1e6) / 1e6
      return waypoints.some(
        w =>
          w.lat != null &&
          w.lng != null &&
          round(w.lat) === round(lat) &&
          round(w.lng) === round(lng),
      )
    },
    [waypoints],
  )

  const addWaypointAt = useCallback(
    (lat: number, lng: number, oddElementType?: string): string | null => {
      if (isDuplicate(lat, lng)) return null
      const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      let placedId: string | null = null
      setWaypoints(prev => {
        const emptyIdx = prev.findIndex(
          w => w.lat == null && w.lng == null && !w.address,
        )
        if (emptyIdx >= 0) {
          placedId = prev[emptyIdx].id
          return prev.map((w, i) =>
            i === emptyIdx
              ? {
                  ...w,
                  lat,
                  lng,
                  address,
                  validated: false,
                  oddElementType: oddElementType ?? null,
                }
              : { ...w, validated: false },
          )
        }
        const newWp = {
          ...createWaypoint(),
          lat,
          lng,
          address,
          oddElementType: oddElementType ?? null,
        }
        placedId = newWp.id
        return prev.map(w => ({ ...w, validated: false })).concat(newWp)
      })
      setValidatedRoute(null)
      setValidatedSegments([])
      setOddElements([])
      setSdsViolations([])
      setValidatedMapName(null)
      return placedId
    },
    [isDuplicate],
  )

  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      const placedId = addWaypointAt(lat, lng)
      if (!placedId) return
      // Start heading selection immediately on marker placement (overwatch behaviour)
      startHeadingSelection(
        placedId,
        document.createElement('div'),
        [],
        false,
        false,
        null,
      )
    },
    [addWaypointAt, startHeadingSelection],
  )

  const handleOddElementClick = useCallback(
    (element: ODDMapElement) => {
      const placedId = addWaypointAt(
        element.location.latitude,
        element.location.longitude,
        element.type,
      )
      if (!placedId) return
      startHeadingSelection(
        placedId,
        document.createElement('div'),
        [],
        false,
        false,
        null,
      )
    },
    [addWaypointAt, startHeadingSelection],
  )

  const handleMarkerDrag = useCallback(
    (index: number, lat: number, lng: number) => {
      setWaypoints(prev =>
        prev.map((w, i) =>
          i === index
            ? {
                ...w,
                lat,
                lng,
                address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                validated: false,
                desiredLat: null,
                desiredLng: null,
                // The ODD element was tied to the waypoint's previous location;
                // moving it invalidates that association, so drop it.
                oddElementType: null,
              }
            : { ...w, validated: false },
        ),
      )
      setValidatedRoute(null)
      setValidatedSegments([])
      setOddElements([])
      setSdsViolations([])
      setValidatedMapName(null)
    },
    [],
  )

  const handleMarkerDragStart = useCallback(
    (waypointId: string) => {
      prepareDragHeadingSelection(waypointId)
    },
    [prepareDragHeadingSelection],
  )

  // Fired at the start of a marker drag (not on release): reset the whole route
  // to the orange "ungenerated" state right away. Position is left untouched here
  // and finalized on dragend via handleMarkerDrag; the dragged waypoint's ODD
  // element is dropped since moving it invalidates that association.
  const handleMarkerMoveStart = useCallback((waypointId: string) => {
    setWaypoints(prev =>
      prev.map(w =>
        w.id === waypointId
          ? { ...w, validated: false, oddElementType: null }
          : { ...w, validated: false },
      ),
    )
    setValidatedRoute(null)
    setValidatedSegments([])
    setOddElements([])
    setSdsViolations([])
    setValidatedMapName(null)
  }, [])

  const resetRouteState = useCallback(() => {
    setWaypoints(prev => prev.map(w => ({ ...w, validated: false })))
    setValidatedRoute(null)
    setValidatedSegments([])
    setOddElements([])
    setMobileyeStatus('idle')
    setValidatedMapName(null)
    setSdsViolations([])
    setRouteComputeError(null)
    setShowAllWaypoints(false)
    setVisuallyExpanded(false)
  }, [])

  const handleWaypointsChange = useCallback(
    (newWaypoints: Waypoint[]) => {
      setWaypoints(newWaypoints.map(w => ({ ...w, validated: false })))
      setValidatedRoute(null)
      setValidatedSegments([])
      setOddElements([])
      setMobileyeStatus('idle')
      setValidatedMapName(null)
      setSdsViolations([])
      setRouteComputeError(null)
      // Cancel heading selection if the pending waypoint was removed or cleared
      // (cleared = still in list but lat/lng wiped — happens when only 2 waypoints remain).
      if (pendingHeadingWaypointId) {
        const pendingWp = newWaypoints.find(
          w => w.id === pendingHeadingWaypointId,
        )
        if (!pendingWp || pendingWp.lat == null || pendingWp.lng == null) {
          cancelHeadingSelection(null)
        }
      }
    },
    [pendingHeadingWaypointId, cancelHeadingSelection],
  )

  const handleMarkerClick = useCallback(
    (waypointId: string) => {
      const wp = waypoints.find(w => w.id === waypointId)
      if (wp?.lat == null || wp?.lng == null) return
      startHeadingSelection(
        waypointId,
        document.createElement('div'),
        [],
        false,
        wp.validated,
        headingDegrees[waypointId] ?? null,
      )
    },
    [waypoints, startHeadingSelection, headingDegrees],
  )

  // Fit to the route once on initial edit open. The fitKey is stable for the
  // lifetime of this editor instance so hasFittedRef in RouteMap fires exactly once.
  // After that first fit, fitWaypoints stays false so user panning isn't overridden.
  const initialFitKeyRef = useRef(
    isEditing && editingTrip?.id ? `edit-${editingTrip.id}` : null,
  )
  const hasFiredInitialFitRef = useRef(false)

  // Imperative fit: directly move the camera when mapInstance becomes available
  // and we have valid waypoints. This fires independently of the prop-based
  // fitWaypoints/fitKey pathway and is robust against render-order races with
  // SavedTripsView's setMapData effect. It runs identically in the regular
  // route builder screen and the vehicle-monitoring assign modal — the modal
  // must fit-to-route the same way, just without the 45° overview tilt (which
  // the flat pitch/bearing 0 below already guarantees).
  // Tracks whether the imperative fit has fired, and whether it fired with a
  // route polyline. Waypoints render first and the polyline (validatedRoute)
  // lands a render later, so we allow exactly one re-fit when the poly first
  // arrives — otherwise the fit locks on the waypoint-only box and a curving
  // route ends up under-framed. Mirrors RouteMap's hadPoly upgrade.
  const imperativeFitRef = useRef<{ fired: boolean; hadPoly: boolean }>({
    fired: false,
    hadPoly: false,
  })
  const correctiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (correctiveTimerRef.current) clearTimeout(correctiveTimerRef.current)
    },
    [],
  )
  useEffect(() => {
    if (!isEditing) return
    if (!mapInstance) return
    // Main route-builder screen: RouteMap's prop-driven fit (fitWaypoints/fitKey,
    // set atomically below) is the SINGLE fit authority. It reveal-gates the
    // route and re-fits on real canvas resize events until the card-swap
    // transition settles, so it frames correctly no matter how far the map's left
    // edge travels (card→edit ~40px vs details→edit ~396px). A second imperative
    // fly here would race that fit on the one shared map — last-writer-wins,
    // nondeterministic — and was the cause of the route landing off to the left
    // when editing a trip preselected in the details panel.
    //
    // The freshly-created assign-trip modal map (fullBleedMap) is different: it
    // isn't sized on mount, so cameraForBounds no-ops until an idle event. The
    // imperative fit's idle-retry handles that, so keep it — modal only.
    if (!fullBleedMap) return
    const valid = waypoints.filter(w => w.lat != null && w.lng != null)
    if (valid.length === 0) return
    const padding = computeMapPadding(true, fullBleedMap)
    const poly = validatedRoute?.coordinates ?? []
    const hasPoly = poly.length >= 2

    // Already fired, and no new polyline geometry to re-frame against → done.
    const st = imperativeFitRef.current
    if (st.fired && (st.hadPoly || !hasPoly)) return

    // Compute + fly to the route framing for the map's CURRENT canvas size.
    // Returns true when it actually moved the camera (camera could be computed),
    // false when the map isn't sized yet (freshly-created modal map — retry on
    // idle).
    const flyToFraming = (duration: number): boolean => {
      if (valid.length >= 2 || poly.length >= 2) {
        // Bounding box over waypoints AND the full route polyline. Extending to
        // the polyline frames a route that curves outside the waypoint box whole,
        // not clipped/under-zoomed at the bends. This matches RouteMap's own
        // prop-driven fit — without it the two fits frame to different bounds and
        // the route lands off-centre.
        const lngs = [...valid.map(w => w.lng!), ...poly.map(([lng]) => lng)]
        const lats = [...valid.map(w => w.lat!), ...poly.map(([, lat]) => lat)]
        const sw: [number, number] = [Math.min(...lngs), Math.min(...lats)]
        const ne: [number, number] = [Math.max(...lngs), Math.max(...lats)]
        // Use cameraForBounds + flyTo instead of fitBounds: fitBounds stores padding
        // in the MapLibre transform, so a second call would see 2× padding and fail.
        // cameraForBounds bakes padding into the returned center/zoom without storing it.
        // The fallback wrapper clamps padding to the modal's narrower canvas, which
        // otherwise can't fit the full panel padding.
        // maxZoom 15.5 so small routes zoom in tight enough to fill the padded
        // viewport rather than floating with large empty side margins.
        const camera = cameraForBoundsWithPaddingFallback(
          mapInstance,
          [sw, ne],
          padding,
          { maxZoom: 15.5, bearing: 0, pitch: 0 },
        )
        if (!camera) return false
        mapInstance.flyTo({
          center: camera.center,
          zoom: camera.zoom,
          pitch: 0,
          bearing: 0,
          duration,
        })
        return true
      }
      mapInstance.flyTo({
        center: [valid[0].lng!, valid[0].lat!],
        zoom: 14,
        pitch: 0,
        bearing: 0,
        duration,
      })
      return true
    }

    // Modal only. The assign-trip modal's floating panels overlay the map rather
    // than resizing it, but the map can still re-layout (e.g. modal open/resize)
    // before it has fully settled. Re-fit on every real `resize` the map emits
    // (each debounced), computed against the CURRENT canvas, so the final fit
    // lands on the settled size. Bounded to a window after open so later
    // user-driven resizes don't yank the camera back after they've panned.
    let corrected = false
    const correctiveWindow = setTimeout(() => {
      corrected = true
      mapInstance.off('resize', onResize)
    }, 1500)
    const onResize = () => {
      if (corrected) return
      if (correctiveTimerRef.current) clearTimeout(correctiveTimerRef.current)
      correctiveTimerRef.current = setTimeout(() => flyToFraming(400), 150)
    }

    const runFit = () => {
      const s = imperativeFitRef.current
      if (s.fired && (s.hadPoly || !hasPoly)) return
      if (!flyToFraming(700)) return
      imperativeFitRef.current = { fired: true, hadPoly: hasPoly }
      mapInstance.on('resize', onResize)
    }

    // Fire immediately — on the regular route builder screen the map is already
    // sized. The freshly-created modal map isn't sized yet (flyToFraming returns
    // false without locking), so also retry on idle.
    runFit()
    if (!imperativeFitRef.current.fired) mapInstance.once('idle', runFit)
    return () => {
      clearTimeout(correctiveWindow)
      mapInstance.off('idle', runFit)
      mapInstance.off('resize', onResize)
    }
  }, [isEditing, waypoints, mapInstance, fullBleedMap, validatedRoute])

  // Push data props into the shared persistent RouteMap (no callbacks — avoids render loops)
  useEffect(() => {
    // Fit once when we first have valid waypoints on an edit session.
    const validCount = waypoints.filter(
      w => w.lat != null && w.lng != null,
    ).length
    const shouldFit =
      !!initialFitKeyRef.current &&
      !hasFiredInitialFitRef.current &&
      validCount >= 1
    if (shouldFit) hasFiredInitialFitRef.current = true
    setMapData({
      center: mapCenter,
      mode: 'editor',
      waypoints,
      routeCoordinates: validatedRoute?.coordinates ?? [],
      violations: validatedRoute?.violations ?? [],
      sdsViolations,
      // Only write fitWaypoints/fitKey when requesting a fit — never write
      // fitWaypoints:false from this effect. React 18 batches this effect's
      // re-runs into one render, and a false written in a later batch member
      // would overwrite the true before RouteMap ever sees it.
      // RouteMap's prevFitKeyRef ensures the fit fires exactly once per fitKey.
      ...(shouldFit && {
        fitWaypoints: true,
        fitKey: initialFitKeyRef.current,
      }),
      oddElements,
      // Raw rows only. SharedMapProvider runs every filter (and holds the
      // control state) through the lib's useAnnotationsState, measuring the
      // "near this route" pass against the `routeCoordinates` set just above —
      // so the layer can never be narrowed to a different route than the one
      // drawn.
      annotationRows,
      vehicleMarker: assigningVehicle
        ? {
            location: assigningVehicle.location,
            name: assigningVehicle.name,
            isPhantom: assigningVehicle.isPhantom,
            heading: assigningVehicle.heading ?? null,
          }
        : undefined,
      headings: headingDegrees,
      pendingHeadingWaypointId,
      // Always set padding so it's in the same state update as fitWaypoints.
      // Full-page: the map sits beside the editor card, so the fit is symmetric.
      // Full-bleed modal: the floating editor panel overlays the map's left edge,
      // so the fit clears it (handled inside computeMapPadding via fullBleedMap).
      padding: computeMapPadding(true, fullBleedMap),
    })
  }, [
    setMapData,
    mapCenter,
    waypoints,
    validatedRoute,
    sdsViolations,
    oddElements,
    annotationRows,
    assigningVehicle,
    headingDegrees,
    pendingHeadingWaypointId,
    fullBleedMap,
  ])

  // Push callbacks into ref — no state, no re-render loop
  useEffect(() => {
    setCallbacks({
      onMapClick: handleMapClick,
      onMarkerClick: handleMarkerClick,
      onMarkerDrag: handleMarkerDrag,
      onMarkerDragStart: handleMarkerDragStart,
      onMarkerMoveStart: handleMarkerMoveStart,
      onOddElementClick: handleOddElementClick,
      onHeadingSelect: (waypointId, bearing) => {
        const wp = waypoints.find(w => w.id === waypointId)
        confirmHeading(waypointId, bearing, null, wp?.validated ?? false)
        setWaypoints(prev => prev.map(w => ({ ...w, validated: false })))
        setValidatedRoute(null)
        setValidatedSegments([])
        setRouteComputeError(null)
      },
      onRemoveHeading: waypointId => {
        removeHeading(waypointId, null)
        cancelHeadingSelection(null)
        setWaypoints(prev => prev.map(w => ({ ...w, validated: false })))
        setValidatedRoute(null)
        setValidatedSegments([])
        setRouteComputeError(null)
      },
    })
  }, [
    setCallbacks,
    handleMapClick,
    handleMarkerClick,
    handleMarkerDrag,
    handleMarkerDragStart,
    handleMarkerMoveStart,
    handleOddElementClick,
    waypoints,
    confirmHeading,
    removeHeading,
    cancelHeadingSelection,
  ])

  // Push overlay content (loading animation + heading toast) into the map column
  useEffect(() => {
    setMapOverlay(
      headingToastVisible && pendingHeadingWaypointId && (
        <div
          css={theme => ({
            position: 'absolute',
            top: theme.space.Space2,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            background: 'rgba(0,0,0,0.75)',
            color: 'white',
            borderRadius: 6,
            padding: `${theme.space.Space1} ${theme.space.Space4}`,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          })}
        >
          Click an arrow to set heading · Esc to cancel
        </div>
      ),
    )
  }, [setMapOverlay, headingToastVisible, pendingHeadingWaypointId])

  // Register MapOverlayOptions props in context so they render at top-right of map.
  // Note: no cleanup here — a null gap between re-runs would flash transparent ODD
  // layers to full opacity for one frame each time oddElements or other deps update.
  // The separate unmount-only effect below clears options when the component leaves.
  useEffect(() => {
    // Auto-enable ODD layers only for the requirement the user confirmed in the
    // VTR selection modal — not for every CB requirement on the ticket. When no
    // requirement is selected yet, fall back to all of them.
    const vtrCbData = effectiveVtr
      ? effectiveVtr.selectedCbIndex !== undefined
        ? effectiveVtr.vtrData.cbData.slice(
            effectiveVtr.selectedCbIndex,
            effectiveVtr.selectedCbIndex + 1,
          )
        : effectiveVtr.vtrData.cbData
      : []
    const vtrRequiredTypes = effectiveVtr
      ? [
          ...new Set(
            vtrCbData.flatMap(cb =>
              cb.oddElementTypes
                .map(
                  (t: string) =>
                    ({
                      Stop: 'STOP_SIGN',
                      Yield: 'YIELD_SIGN',
                      'No Entry': 'NO_ENTRY',
                      'Pedestrian Crossing': 'PEDESTRIAN_CROSSING',
                      'Railroad Crossing': 'RAILROAD_CROSSING',
                      'No Turn on Red': 'NO_TURN_ON_RED',
                      'One Way': 'ONE_WAY',
                      Roundabout: 'ROUNDABOUT',
                      'Traffic Light': 'TRAFFIC_LIGHT',
                    })[t],
                )
                .filter((k): k is string => k != null),
            ),
          ),
        ]
      : []
    setOverlayOptions({
      metropolisId,
      metropolisName,
      autoEnableLanes: true,
      autoEnableGeonets: true,
      onOddElementClick: (lat: number, lng: number, type: string) => {
        const placedId = addWaypointAt(lat, lng, type)
        if (!placedId) return
        startHeadingSelection(
          placedId,
          document.createElement('div'),
          [],
          false,
          false,
          null,
        )
      },
      routeOddElements: oddElements,
      vtrRequiredTypes,
    })
  }, [
    setOverlayOptions,
    metropolisId,
    metropolisName,
    addWaypointAt,
    startHeadingSelection,
    oddElements,
    effectiveVtr,
  ])

  // No unmount cleanup for overlayOptions: SavedTripsView re-registers its options
  // on the next render after TripEditorView unmounts (its effect re-runs because
  // location.pathname just changed back to saved-trips). Adding a cleanup here
  // would set options to null for one render → hasRoute=false → transparent ODD
  // layers flash to full opacity.

  // Clear overlay on unmount so it doesn't persist on saved-trips view
  const mapCenterRef = useRef(mapCenter)
  mapCenterRef.current = mapCenter
  const setMapDataRef = useRef(setMapData)
  setMapDataRef.current = setMapData
  const setMapOverlayRef = useRef(setMapOverlay)
  setMapOverlayRef.current = setMapOverlay
  useEffect(() => {
    return () => {
      // Reset the shared map to defaults when the editor closes so a stale route
      // doesn't linger on the saved-trips view — but PRESERVE the fit signals
      // (fitWaypoints/fitKey). They're one-shot fit *commands*, not state: the
      // view that mounts next re-issues them. Forcing them to false/null here
      // fires RouteMap's fit-RESET branch, which clears the pending fit timer and
      // cancels a fit the incoming view just armed. That happens on a remount
      // (React StrictMode's mount→unmount→mount, or any transient remount) right
      // after navigating into edit, leaving the edit flow un-fitted while the
      // details flow — which never mounts this component — stays fine.
      const { fitWaypoints: _fw, fitKey: _fk, ...resetData } = DEFAULT_MAP_DATA
      setMapDataRef.current({
        ...resetData,
        center: mapCenterRef.current,
      })
      setMapOverlayRef.current(null)
    }
  }, [])

  const TRIP_TYPE_LABEL_MAP: Partial<Record<TripType, string>> = {
    [TripType.TripTypeVnv]: 'V&V',
    [TripType.TripTypeGoods]: 'Goods',
    [TripType.TripTypeInternal]: 'V&V',
    [TripType.TripTypePassenger]: 'Passenger',
  }
  const tripTypeLabel = effectiveVtr
    ? 'Route'
    : (TRIP_TYPE_LABEL_MAP[effectiveTripType] ?? 'Route')

  return (
    <>
      {/* Header */}
      <div
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
          padding: `${theme.space.Space3} ${theme.space.Space4}`,
          borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          flexShrink: 0,
        })}
      >
        <IconButton
          aria-label="Back to saved trips"
          size="compact"
          variant="plain"
          onClick={() => handleBack()}
          icon={<ArrowLeftIcon />}
        />
        <Heading size="sm" css={{ flex: 1 }}>
          {headerTitle !== undefined
            ? headerTitle
            : isAssigning
              ? `Assign to ${assigningVehicle!.name}`
              : isEditing
                ? 'Editing Route'
                : 'Building Route'}
        </Heading>
      </div>

      {/* VTR ticket selector — fixed above the scrollable area */}
      {!isAssigning && !isEditing && (
        <div css={{ flexShrink: 0 }}>
          {effectiveVtr ? (
            <div
              css={{
                animation: 'vtrSlideIn 220ms ease',
                '@keyframes vtrSlideIn': {
                  from: { opacity: 0, transform: 'translateY(-6px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <VtrRequirements
                vtrData={effectiveVtr.vtrData}
                waypoints={waypoints}
                metropolisName={metropolisName}
                selectedCbIndex={effectiveVtr.selectedCbIndex}
                similarRoutes={similarRoutes}
                generating={generatingRoute}
                generateInMapArea={generateInMapArea}
                onGenerateInMapAreaChange={e =>
                  setGenerateInMapArea(e.target.checked)
                }
                onAutoGenerate={canGenerateFromVtr ? handleCompute : undefined}
                onSimilarRouteClick={id =>
                  navigate(
                    RouteBuilderAreaRoutes.EditTrip.route.replace(':id', id),
                  )
                }
                onClear={() => {
                  if (onClearVtr) {
                    onClearVtr()
                  } else {
                    setLocalVtr(null)
                  }
                }}
              />
            </div>
          ) : (
            <div
              css={theme => ({
                padding: `${theme.space.Space3} ${theme.space.Space4}`,
                borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
              })}
            >
              <VtrInlineSelector
                metropolisName={metropolisName}
                onConfirm={(vtrData, cbIndex, ticketKey) => {
                  setLocalVtr({
                    vtrData,
                    ticketKey,
                    selectedCbIndex: cbIndex,
                  })
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Main area — two layout modes:
          Collapsed (route exists, show all not pressed):
            overflow:hidden, no scroll. Top content fills with flex:1.
            Route summary pinned at the bottom as a flex sibling.
          Expanded / no route:
            overflow:auto, full scroll column. */}
      <div
        ref={scrollContainerRef}
        css={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // hidden when measuring/clipping (not-expanded + overflow unknown or confirmed) or animating
          overflow:
            (validatedRoute &&
              !isAssigning &&
              !showAllWaypoints &&
              waypointListOverflows !== false) ||
            waypointAnimHeight !== null
              ? 'hidden'
              : 'auto',
        }}
      >
        {/* Scrollable/clipped content column */}
        <div
          css={theme => ({
            padding: theme.space.Space4,
            // No bottom padding only when in-scroll summary is actually present (expanded + confirmed overflow)
            ...(waypointAnimHeight === null &&
            validatedRoute &&
            !isAssigning &&
            waypointListOverflows === true &&
            showAllWaypoints
              ? { paddingBottom: 0 }
              : {}),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space4,
            // Route exists, not expanded, not animating: constrain for measurement.
            // Clip only when overflow is unknown (null) or confirmed (true) — not when confirmed no-overflow.
            ...(validatedRoute &&
            !isAssigning &&
            !showAllWaypoints &&
            waypointAnimHeight === null
              ? waypointListOverflows !== false
                ? { flex: 1, minHeight: 0, overflow: 'hidden' }
                : { flex: 1, minHeight: 0 }
              : {}),
          })}
        >
          {/* Route type + status badge row */}
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <div
              css={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: theme.space.Space2,
              })}
            >
              <Heading size="md" css={{ fontSize: 'calc(1em + 4px)' }}>
                {tripTypeLabel}
              </Heading>
              <Badge
                label={isEditing ? 'Editing' : 'Creating'}
                variant="neutral"
                icon={<PenIcon />}
              />
            </div>
            <IconButton
              aria-label="Copy locations to clipboard"
              size="compact"
              variant="plain"
              onClick={handleCopyLocations}
              disabled={validWaypoints.length === 0}
              icon={<CopyIcon />}
              css={{
                opacity: validWaypoints.length === 0 ? 0.3 : 1,
                transition: 'opacity 150ms ease',
              }}
            />
          </div>

          {!isAssigning && (loadingFleets || visibleFleets.length > 0) && (
            <div
              css={theme => ({
                marginTop: `-${theme.space.Space1}`,
                marginBottom: theme.space.Space1,
                flexShrink: 0,
                position: 'relative',
                '& [class*="arrow"]': { transition: 'transform 200ms ease' },
                ...(fleetSelectOpen
                  ? {
                      '& [class*="arrow"]': {
                        transform: 'rotate(180deg)',
                        transition: 'transform 200ms ease',
                      },
                    }
                  : {}),
              })}
            >
              <Select<string>
                aria-label="Fleet"
                value={fleetId}
                disabled={loadingFleets}
                onClick={() => setFleetSelectOpen(o => !o)}
                onBlur={() =>
                  requestAnimationFrame(() => setFleetSelectOpen(false))
                }
                onChange={value => {
                  if (value) setSelectedFleetId(value)
                }}
                renderValue={value =>
                  loadingFleets
                    ? ''
                    : (visibleFleets.find(
                        f => (f.integrationId || f.id) === value,
                      )?.name ?? value)
                }
                placeholder={loadingFleets ? 'Loading fleets...' : undefined}
                options={visibleFleets.map(f => (
                  <Option<string> key={f.id} value={f.integrationId || f.id}>
                    {f.name}
                  </Option>
                ))}
              />
              {loadingFleets && (
                <div
                  css={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <LoadingIndicator size={16} />
                </div>
              )}
            </div>
          )}

          {/* Waypoint list — clipped to available flex space when collapsed */}
          <div
            ref={waypointWrapperRef}
            css={{
              position: 'relative',
              overflow: 'hidden',
              transition: `height ${EXPAND_MS}ms ease`,
              // Heading-direction arrows orbit ~14px outside the badge boundary.
              // Extend the wrapper leftward (paddingLeft + negative marginLeft keeps content
              // visually aligned) and add paddingTop so arrows on the first waypoint aren't
              // clipped at the top edge. The flex gap above this wrapper absorbs the top padding.
              paddingTop: 14,
              paddingLeft: 14,
              marginLeft: -14,
              // During animation: explicit px height drives the transition.
              // Collapsed (no animation): flex:1 fills available space.
              // Expanded (no animation): height:auto (natural).
              ...(waypointAnimHeight !== null
                ? { height: waypointAnimHeight }
                : // null = unmeasured, true = overflow confirmed: constrain for measurement / clipping
                  validatedRoute &&
                    !isAssigning &&
                    !showAllWaypoints &&
                    waypointListOverflows !== false
                  ? { flex: 1, minHeight: 0 }
                  : { height: 'auto' }),
            }}
          >
            <WaypointList
              waypoints={waypoints}
              onChange={handleWaypointsChange}
              onReorderStart={resetRouteState}
              onClearHeading={waypointId => removeHeading(waypointId, null)}
              onSetHeadings={setHeadings}
              violations={validatedRoute?.violations ?? []}
              headings={headingDegrees}
            />
            {/* Gradient fade at the bottom of clipped waypoint list */}
            {waypointListOverflows === true && (
              <div
                css={theme => ({
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 64,
                  zIndex: 4,
                  pointerEvents: 'none',
                  background: `linear-gradient(to bottom, transparent 0%, ${theme.semantic.ColorBackgroundDefault} 100%)`,
                  opacity: !visuallyExpanded ? 1 : 0,
                  transition: `opacity ${EXPAND_MS}ms ease`,
                })}
              />
            )}
          </div>

          {/* Show All / Show Less buttons — cross-fade during animation */}
          {waypointListOverflows === true && validatedRoute && !isAssigning && (
            <div
              css={{
                width: '100%',
                flexShrink: 0,
                marginTop: -10,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                label={
                  visuallyExpanded
                    ? 'Show Less'
                    : `Show All (${waypoints.length})`
                }
                variant="plain"
                size="compact"
                icon={
                  <div
                    css={{
                      display: 'flex',
                      transition: 'transform 200ms ease',
                      transform: visuallyExpanded
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    }}
                  >
                    <ChevronDownIcon />
                  </div>
                }
                onClick={() =>
                  visuallyExpanded ? collapseWaypoints() : expandWaypoints()
                }
                css={{
                  color: '#0532ff',
                  '&:hover, &:focus': {
                    backgroundColor: 'transparent !important',
                  },
                  '&::before, &::after': { display: 'none' },
                }}
              />
            </div>
          )}

          {/* Add Destination — always visible, outside the clipped region */}
          {!isAssigning && (
            <div
              ref={addDestinationRef}
              css={{ alignSelf: 'flex-start', flexShrink: 0, marginTop: -18 }}
            >
              <Button
                label="Add Destination"
                variant="plain"
                size="compact"
                icon={<PlusIcon />}
                onClick={addWaypoint}
              />
            </div>
          )}

          {canSetAllWaypointsMode && (
            <div css={{ alignSelf: 'flex-start', flexShrink: 0 }}>
              <Checkbox
                label={t('routeBuilder_allWaypointsMode_checkbox')}
                checked={allWaypointsMode}
                onChange={e => setAllWaypointsMode(e.target.checked)}
              />
            </div>
          )}

          {/* Settings panel — only visible when not in collapsed-route mode */}
          {showSettings && !isCollapsedMode && (
            <div
              css={theme => ({
                padding: theme.space.Space3,
                borderRadius: 8,
                backgroundColor: theme.semantic.ColorBackgroundDimmedReduced,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space.Space3,
                flexShrink: 0,
              })}
            >
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text size="sm">Use stop resolver</Text>
                <Switch
                  aria-label="Use stop resolver"
                  size="small"
                  checked={useStopResolver}
                  onChange={setUseStopResolver}
                />
              </div>
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text size="sm">Add a passenger</Text>
                <Switch
                  aria-label="Add a passenger"
                  size="small"
                  checked={addPassenger}
                  onChange={setAddPassenger}
                />
              </div>
            </div>
          )}

          {/* Route compute error */}
          {routeComputeError && !isAssigning && (
            <div
              css={theme => ({
                padding: `${theme.space.Space2} ${theme.space.Space3}`,
                backgroundColor: theme.semantic.ColorCriticalAlternative,
                borderLeft: `3px solid ${theme.semantic.ColorCriticalDefault}`,
                borderRadius: 4,
                flexShrink: 0,
              })}
            >
              <Text
                size="sm"
                css={theme => ({ color: theme.semantic.ColorCriticalDefault })}
              >
                {routeComputeError}
              </Text>
            </div>
          )}

          {/* Route summary — in scroll flow only when fully expanded (not animating) and confirmed overflow */}
          {validatedRoute &&
            !isAssigning &&
            showAllWaypoints &&
            waypointListOverflows === true &&
            waypointAnimHeight === null && (
              <div
                css={theme => ({
                  marginLeft: `-${theme.space.Space4}`,
                  marginRight: `-${theme.space.Space4}`,
                  flexShrink: 0,
                })}
              >
                <GeneratedRouteSummary
                  validatedRoute={validatedRoute}
                  oddElements={oddElements}
                  fetchingOdds={fetchingOdds}
                  mobileyeStatus={mobileyeStatus}
                  validatingSds={validatingSds}
                  sdsViolations={sdsViolations}
                  validatedMapName={validatedMapName}
                  fleetId={fleetId}
                  isSaving={isSavingSavedTrip}
                  hasViolations={hasViolations}
                  onMobileyeValidation={handleMobileyeValidation}
                  onSave={saveDialog.openDialog}
                />
              </div>
            )}
        </div>

        {/* Route summary pinned at the bottom — when assigning, collapsed, animating, or confirmed no overflow */}
        {validatedRoute &&
          (isAssigning ||
            !showAllWaypoints ||
            waypointListOverflows !== true ||
            waypointAnimHeight !== null) && (
            <GeneratedRouteSummary
              validatedRoute={validatedRoute}
              oddElements={oddElements}
              fetchingOdds={fetchingOdds}
              mobileyeStatus={mobileyeStatus}
              validatingSds={validatingSds}
              sdsViolations={sdsViolations}
              validatedMapName={validatedMapName}
              fleetId={fleetId}
              isSaving={isSavingSavedTrip}
              hasViolations={hasViolations}
              onMobileyeValidation={handleMobileyeValidation}
              onSave={saveDialog.openDialog}
              assigningVehicle={isAssigning ? assigningVehicle : undefined}
              onAssign={isAssigning ? handleAssign : undefined}
              assigning={isAssigning ? assigning : undefined}
            />
          )}
      </div>

      {/* Footer: gear | Cancel | primary action */}
      <div
        css={theme => ({
          padding: `${theme.space.Space3} ${theme.space.Space4}`,
          borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
          flexShrink: 0,
        })}
      >
        <IconButton
          type="button"
          aria-label="Route settings"
          size="compact"
          variant={showSettings ? 'primary' : 'secondary'}
          onClick={() => setShowSettings(prev => !prev)}
          icon={<SettingsIcon />}
        />
        <Button
          type="button"
          label="Cancel"
          variant="secondary"
          size="compact"
          onClick={() => handleBack()}
          css={{ flex: 1 }}
        />
        {(!validatedRoute || hasViolations) && (
          <Button
            type="button"
            label={computing ? 'Generating...' : 'Generate Route'}
            variant="primary"
            size="compact"
            onClick={handleCompute}
            disabled={!canCompute || computing}
            icon={computing ? <LoadingIndicator size={16} /> : undefined}
            css={{ flex: 2 }}
          />
        )}
      </div>

      {/* Save modal — collects name / description / fleet ID */}
      {saveDialog.isOpen &&
        createPortal(
          <div
            css={{
              position: 'fixed',
              inset: 0,
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onClick={e => {
              if (e.target === e.currentTarget) saveDialog.closeDialog()
            }}
          >
            <div
              css={theme => ({
                width: 520,
                backgroundColor: theme.semantic.ColorBackgroundDefault,
                borderRadius: theme.radius.RadiusLarge,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
              })}
            >
              <div
                css={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: `${theme.space.Space3} ${theme.space.Space4}`,
                  borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                  flexShrink: 0,
                })}
              >
                <Heading size="sm">
                  {isEditing ? 'Update Route' : 'Save Route'}
                </Heading>
                <IconButton
                  aria-label="Close"
                  size="compact"
                  variant="plain"
                  icon={<XIcon />}
                  onClick={saveDialog.closeDialog}
                />
              </div>
              {effectiveVtr && (
                <div
                  css={theme => ({
                    padding: `${theme.space.Space3} ${theme.space.Space4}`,
                    borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.space.Space3,
                  })}
                >
                  <VtrMetaRow
                    label="Associated V&V Ticket"
                    value={effectiveVtr.ticketKey}
                    onInfo={() => setShowVtrInfo(true)}
                  />
                  {(() => {
                    const cb =
                      effectiveVtr.selectedCbIndex !== undefined
                        ? effectiveVtr.vtrData.cbData[
                            effectiveVtr.selectedCbIndex
                          ]
                        : effectiveVtr.vtrData.cbData[0]
                    return (
                      <>
                        {cb?.fleetTarget && (
                          <VtrMetaRow
                            label="Associated Code Beamer Test Case"
                            value={cb.fleetTarget}
                          />
                        )}
                        {cb?.codebeamerTestcaseId && (
                          <VtrMetaRow
                            label="Code Beamer ID"
                            value={`#${cb.codebeamerTestcaseId}`}
                          />
                        )}
                      </>
                    )
                  })()}
                </div>
              )}
              <form
                id="route-builder-save-form"
                onSubmit={handleSubmit(handleSave)}
                css={theme => ({
                  padding: theme.space.Space4,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.space.Space3,
                })}
              >
                <FormTextfield
                  control={control}
                  name="name"
                  label="Name"
                  rules={{ required }}
                  disabled={isSavingSavedTrip}
                  autoFocus
                />
                <FormTextarea
                  control={control}
                  name="description"
                  label="Description"
                  disabled={isSavingSavedTrip}
                />
                {effectiveAllWaypointsMode && <AllWaypointsModeIndicator />}
              </form>
              <div
                css={theme => ({
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: theme.space.Space2,
                  padding: `${theme.space.Space3} ${theme.space.Space4}`,
                  borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                })}
              >
                <Button
                  label="Cancel"
                  variant="secondary"
                  onClick={saveDialog.closeDialog}
                />
                <Button
                  label={
                    isSavingSavedTrip
                      ? isEditing
                        ? 'Updating...'
                        : 'Saving...'
                      : isEditing
                        ? 'Update'
                        : 'Save'
                  }
                  variant="primary"
                  type="submit"
                  form="route-builder-save-form"
                  disabled={isSavingSavedTrip || !routeName.trim()}
                />
              </div>
            </div>
            {showVtrInfo && effectiveVtr && (
              <VtrSelectionModal
                vtrData={effectiveVtr.vtrData}
                initialCbIndex={effectiveVtr.selectedCbIndex ?? 0}
                readOnly
                onConfirm={() => setShowVtrInfo(false)}
                onCancel={() => setShowVtrInfo(false)}
              />
            )}
          </div>,
          document.body,
        )}
    </>
  )
}

const VtrMetaRow = ({
  label,
  value,
  onInfo,
}: {
  label: string
  value: string
  onInfo?: () => void
}) => (
  <div css={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <Text size="sm" color="reduced">
      {label}
    </Text>
    <div css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Text size="sm" weight="bold">
        {value}
      </Text>
      {onInfo && (
        <button
          type="button"
          aria-label="VTR details"
          onClick={onInfo}
          css={theme => ({
            background: 'none',
            border: 'none',
            padding: 2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: theme.semantic.ColorContentReduced,
            '&:hover': { color: theme.semantic.ColorContentDefault },
          })}
        >
          <Icon
            size={16}
            css={{ transform: 'scale(0.85)', transformOrigin: 'center' }}
          >
            <InfoIIcon />
          </Icon>
        </button>
      )}
    </div>
  </div>
)
